import { adapterExecutionResultSchema, type AdapterExecutionResult } from './types.js';

const FORBIDDEN_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const MAX_DEPTH = 8;
const MAX_OBJECT_KEYS = 160;
const MAX_ARRAY_LENGTH = 600;
const MAX_STRING_LENGTH = 4_000;
const MAX_SERIALIZED_BYTES = 512 * 1024;

export class MalformedAdapterOutputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MalformedAdapterOutputError';
  }
}

export function validateAdapterOutput(value: unknown): AdapterExecutionResult {
  const sanitized = sanitizeUntrustedValue(value);
  const serialized = JSON.stringify(sanitized);
  if (serialized.length > MAX_SERIALIZED_BYTES) {
    throw new MalformedAdapterOutputError('Adapter output exceeds the 512 KiB limit');
  }
  const parsed = adapterExecutionResultSchema.safeParse(sanitized);
  if (!parsed.success) {
    throw new MalformedAdapterOutputError('Adapter output does not satisfy AdapterExecutionResult');
  }
  return parsed.data;
}

function sanitizeUntrustedValue(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) throw new MalformedAdapterOutputError('Adapter output exceeds the depth limit');
  if (value === null || value === undefined || typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new MalformedAdapterOutputError('Adapter output contains a non-finite number');
    return value;
  }
  if (typeof value === 'string') return value.slice(0, MAX_STRING_LENGTH);
  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_LENGTH) throw new MalformedAdapterOutputError('Adapter output array is oversized');
    return value.map((entry) => sanitizeUntrustedValue(entry, depth + 1));
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length > MAX_OBJECT_KEYS) throw new MalformedAdapterOutputError('Adapter output object is oversized');
    const output = Object.create(null) as Record<string, unknown>;
    for (const [key, entry] of entries) {
      if (FORBIDDEN_KEYS.has(key)) throw new MalformedAdapterOutputError(`Forbidden adapter output key: ${key}`);
      output[key] = sanitizeUntrustedValue(entry, depth + 1);
    }
    return output;
  }
  throw new MalformedAdapterOutputError('Adapter output contains an unsupported value');
}
